import { Trans } from 'react-i18next';
import { match, Pattern } from 'ts-pattern';

import type { InvoiceHistoryRecord } from '~/api/invoices/types';

import { Text, Timeline } from '~/components';
import { useIntl } from '~/hooks/useIntl';

import { remarkStyle, remarkTitleStyle } from './InvoiceTimeline.css';
import { followUpStepTypes, historyRecordIcons } from './InvoiceTimeline.utils';
import { RemarkAuthorLabel } from './RemarkAuthorLabel';

type Props = {
  record: InvoiceHistoryRecord;
};

export const InvoiceHistoryItem = ({ record }: Props) => {
  const { formatCurrency, formatDate } = useIntl();

  const title = match(record)
    .with({ type: 'Approved', attributes: { approvalType: 'automatic' } }, () => (
      <Trans i18nKey="invoices:details.history.message.Approved.instant" />
    ))
    .with(
      { type: Pattern.union('EmailApprovedAndSent', 'CreditNoteEmailApprovedAndSent'), attributes: Pattern.nonNullable },
      ({ attributes, type }) => <Trans i18nKey={`invoices:details.history.message.${type}.${attributes.recipient}`} />,
    )
    .with({ type: 'EmailPaymentReminder', attributes: Pattern.nonNullable }, ({ attributes }) => (
      <Trans i18nKey={`invoices:details.history.message.EmailPaymentReminder.${attributes.reminderType}`} />
    ))
    .with(
      {
        type: Pattern.union(
          'PeppolCoAccepted',
          'PeppolCoDelivered',
          'PeppolCoRefused',
          'PeppolCoSent',
          'PeppolFcAccepted',
          'PeppolFcDelivered',
          'PeppolFcRefused',
          'PeppolFcSent',
        ),
        attributes: Pattern.nonNullable,
      },
      ({ attributes, type }) => (
        <Trans i18nKey={`invoices:details.history.message.${type}.${attributes.document === 'invoice' ? 'default' : 'creditNote'}`} />
      ),
    )
    .with({ type: 'PartiallyPaid', attributes: Pattern.nonNullable }, ({ attributes }) => (
      <Trans i18nKey="invoices:details.history.message.PartiallyPaid.default" values={{ amount: formatCurrency(attributes.amount) }} />
    ))
    .with({ type: 'FollowUpRemark' }, ({ attributes }) => <RemarkAuthorLabel remark={attributes.remark} />)
    .otherwise(({ type }) => <Trans i18nKey={`invoices:details.history.message.${type}.default`} />);

  const content = match(record)
    .with({ type: Pattern.union(...followUpStepTypes) }, ({ attributes, type }) => (
      <>
        <Trans components={{ br: <br /> }} i18nKey={`invoices:details.paymentInfoDetail.types.${type}.description`} />

        {attributes.remark && (
          <div className={remarkStyle}>
            <p className={remarkTitleStyle}>
              <RemarkAuthorLabel remark={attributes.remark} />
            </p>

            <Text>{attributes.remark.message}</Text>
          </div>
        )}
      </>
    ))
    .with({ type: 'FollowUpRemark' }, ({ attributes }) =>
      attributes.remark ? (
        <div className={remarkStyle}>
          <Text>{attributes.remark.message}</Text>
        </div>
      ) : null,
    )
    .otherwise(() => null);

  return (
    <Timeline.Item content={content} done hint={formatDate(record.timestamp)} icon={historyRecordIcons[record.type]}>
      {title}
    </Timeline.Item>
  );
};
